import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  Trophy,
  TrendingUp,
  Calendar,
  MapPin,
  Target,
  AlertCircle,
  Plus,
  Loader2,
} from "lucide-react";

const Dashboard = ({ jobs, onUpdateJob, onAddJob, onNavigateToJobs }) => {
  const handleStatusClick = (status) => {
    if (onNavigateToJobs) {
      onNavigateToJobs(status);
    }
  };

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Calculate statistics from jobs array
  const calculateStats = (jobs) => {
    const total = jobs.length;
    // Calculate application status breakdown
    const notApplied = jobs.filter(
      (j) => j.applicationStatus === "Not Applied",
    ).length;
    const applied = jobs.filter(
      (j) => j.applicationStatus === "Applied",
    ).length;
    const interviews = jobs.filter((j) =>
      ["Interview Scheduled", "Interview Completed"].includes(
        j.applicationStatus,
      ),
    ).length;
    const offers = jobs.filter(
      (j) => j.applicationStatus === "Offer Received",
    ).length;
    const rejected = jobs.filter(
      (j) => j.applicationStatus === "Rejected",
    ).length;

    // Calculate location breakdown
    const locationBreakdown = jobs.reduce((acc, job) => {
      const location = job.location || "Unknown";
      acc[location] = (acc[location] || 0) + 1;
      return acc;
    }, {});

    // Calculate role breakdown
    const roleBreakdown = jobs.reduce((acc, job) => {
      const role = job.jobTitle || "Unknown";
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    }, {});

    // Calculate application momentum
    const now = new Date();
    const fourWeeksAgo = new Date(now.getTime() - 4 * 7 * 24 * 60 * 60 * 1000);
    const recentJobs = jobs.filter(
      (job) => new Date(job.dateAdded) >= fourWeeksAgo,
    );

    const weeklyData = [];
    for (let i = 3; i >= 0; i--) {
      const weekStart = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      const weekJobs = jobs.filter((job) => {
        const jobDate = new Date(job.dateAdded);
        return jobDate >= weekStart && jobDate < weekEnd;
      });
      weeklyData.push({
        week: `Week ${4 - i}`,
        count: weekJobs.length,
        date: weekStart.toLocaleDateString(),
      });
    }

    return {
      total,
      notApplied,
      applied,
      interviews,
      offers,
      rejected,
      responseRate: total > 0 ? Math.round((applied / total) * 100) : 0,
      interviewRate: applied > 0 ? Math.round((interviews / applied) * 100) : 0,
      offerRate: interviews > 0 ? Math.round((offers / interviews) * 100) : 0,
      locationBreakdown,
      roleBreakdown,
      weeklyData,
      recentJobs: recentJobs.length,
      avgPerWeek: recentJobs.length / 4,
    };
  };

  useEffect(() => {
    setLoading(true);
    const calculatedStats = calculateStats(jobs);
    setStats(calculatedStats);
    setLoading(false);
  }, [jobs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-zinc-400 mx-auto mb-4" />
          <p className="text-zinc-600">No data available</p>
        </div>
      </div>
    );
  }

  const totalJobs = stats.total;

  return (
    <div className="min-h-screen bg-zinc-50 relative overflow-hidden">
      {/* Clean background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(#E4E4E7_1px,transparent_1px)] [background-size:16px_16px] opacity-30" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative h-64 bg-white rounded-xl border border-zinc-200 p-8 mb-8 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-zinc-50 rounded-xl" />

          <div className="relative z-10">
            <h1 className="text-5xl font-black font-display text-zinc-900 mb-2 tracking-tight">
              Your Job Hunt Dashboard
            </h1>
            <p className="text-zinc-600 text-xl font-display">
              You're crushing it! Keep momentum going
            </p>
          </div>

          {/* Floating elements */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="absolute right-8 top-8 w-16 h-16 bg-blue-100 rounded-full"
          />
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
            className="absolute right-24 bottom-8 w-12 h-12 bg-zinc-100 rounded-full"
          />
        </motion.div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-4 gap-6 auto-rows-fr mb-8">
          {/* Total Applications - Big Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="md:col-span-2 md:row-span-2"
          >
            <div className="h-full bg-white rounded-xl border border-zinc-200 p-8 hover:border-zinc-300 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-zinc-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-sm font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                      Total Applications
                    </h2>
                    <p className="text-6xl font-black font-display text-zinc-900">
                      {totalJobs}
                    </p>
                  </div>
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-8 h-8 text-blue-600" />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  <span className="text-blue-500 font-semibold">
                    {stats.responseRate}% response rate
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Interviews */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:col-span-2 md:row-span-1"
          >
            <div className="h-full bg-white rounded-xl border border-zinc-200 p-6 hover:border-zinc-300 transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                    Interviews
                  </h3>
                  <p className="text-3xl font-black font-display text-zinc-900">
                    {stats.interviews}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Offers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="md:col-span-2 md:row-span-1"
          >
            <div className="h-full bg-white rounded-xl border border-zinc-200 p-6 hover:border-zinc-300 transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                    Offers
                  </h3>
                  <p className="text-3xl font-black font-display text-zinc-900">
                    {stats.offers}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Application Status Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="md:col-span-4 md:row-span-1"
          >
            <div className="h-full bg-white rounded-xl border border-zinc-200 p-6">
              <h3 className="text-lg font-black font-display text-zinc-900 mb-4">
                Application Status
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  onClick={() => handleStatusClick("Not Applied")}
                  className="p-4 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors text-left"
                >
                  <div className="text-2xl font-black text-zinc-900">
                    {stats.notApplied}
                  </div>
                  <div className="text-sm text-zinc-600">Not Applied</div>
                </button>
                <button
                  onClick={() => handleStatusClick("Applied")}
                  className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-left"
                >
                  <div className="text-2xl font-black text-blue-600">
                    {stats.applied}
                  </div>
                  <div className="text-sm text-blue-600">Applied</div>
                </button>
                <button
                  onClick={() => handleStatusClick("Interview Scheduled")}
                  className="p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors text-left"
                >
                  <div className="text-2xl font-black text-amber-600">
                    {stats.interviews}
                  </div>
                  <div className="text-sm text-amber-600">Interviews</div>
                </button>
                <button
                  onClick={() => handleStatusClick("Offer Received")}
                  className="p-4 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors text-left"
                >
                  <div className="text-2xl font-black text-emerald-600">
                    {stats.offers}
                  </div>
                  <div className="text-sm text-emerald-600">Offers</div>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Jobs by Location */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="md:col-span-2 md:row-span-1"
          >
            <div className="h-full bg-white rounded-xl border border-zinc-200 p-6 hover:border-zinc-300 transition-all duration-300">
              <h3 className="text-lg font-black font-display text-zinc-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-500" />
                Jobs by Location
              </h3>
              <div className="space-y-3">
                {Object.entries(stats.locationBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 3)
                  .map(([location, count], index) => (
                    <div
                      key={location}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm font-medium text-zinc-700">
                        {location}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-zinc-900">
                          {count}
                        </span>
                        <div className="w-20 h-2 bg-zinc-200 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(count / totalJobs) * 100}%` }}
                            transition={{
                              duration: 1,
                              delay: 0.6 + index * 0.1,
                            }}
                            className="h-full bg-blue-500 rounded-full"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </motion.div>

          {/* Jobs by Role */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="md:col-span-2 md:row-span-1"
          >
            <div className="h-full bg-white rounded-xl border border-zinc-200 p-6 hover:border-zinc-300 transition-all duration-300">
              <h3 className="text-lg font-black font-display text-zinc-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-500" />
                Jobs by Role
              </h3>
              <div className="space-y-3">
                {Object.entries(stats.roleBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 3)
                  .map(([role, count], index) => (
                    <div
                      key={role}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm font-medium text-zinc-700 truncate">
                        {role}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-zinc-900">
                          {count}
                        </span>
                        <div className="w-20 h-2 bg-zinc-200 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(count / totalJobs) * 100}%` }}
                            transition={{
                              duration: 1,
                              delay: 0.7 + index * 0.1,
                            }}
                            className="h-full bg-blue-500 rounded-full"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </motion.div>

          {/* Application Momentum */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="md:col-span-4 md:row-span-1"
          >
            <div className="h-full bg-white rounded-xl border border-zinc-200 p-6">
              <h3 className="text-lg font-black font-display text-zinc-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Application Momentum
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-zinc-600">This Week</p>
                  <p className="text-2xl font-black text-blue-500">
                    {stats.weeklyData[3]?.count || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-zinc-600">Avg/Week</p>
                  <p className="text-2xl font-black text-zinc-900">
                    {stats.avgPerWeek.toFixed(1)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-zinc-600">Last 4 Weeks</p>
                  <p className="text-2xl font-black text-zinc-900">
                    {stats.recentJobs}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Floating Add Job Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onAddJob}
          className="fixed bottom-8 right-8 w-16 h-16 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
        >
          <Plus className="w-8 h-8" />
        </motion.button>
      </div>
    </div>
  );
};

export default Dashboard;
